import { useEffect, useId, useRef, useState } from 'react'
import { Button, Icon, IconButton, LangSwitch } from '../../design-system/index.js'
import { NAV_HREFS } from '../../content/landing'
import { wrap } from '../layout.js'
import './SiteHeader.css'

/**
 * Sticky header: wordmark, nav (desktop), language switch and the primary CTA,
 * set in a floating pill over the hero's backdrop.
 *
 * On mobile the CTA is replaced by a menu button, which opens a disclosure
 * holding the same nav links — the design shows the button but has nowhere for
 * it to lead, and a control that does nothing is unusable by keyboard and
 * screen-reader users.
 */
export function SiteHeader({ t, lang, onLangChange, desktop, authHref, signedIn = false }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuId = useId()
  const toggleRef = useRef(null)

  // The menu only exists in the mobile layout.
  useEffect(() => {
    if (desktop) setMenuOpen(false)
  }, [desktop])

  useEffect(() => {
    if (!menuOpen) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        toggleRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [menuOpen])

  return (
    <header className="u-header">
      <div style={{ ...wrap(desktop), paddingTop: 12, paddingBottom: 12 }}>
        <div
          className="u-header-bar"
          style={{
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: desktop ? '0 10px 0 24px' : '0 8px 0 18px',
          }}
        >
          <a href="#top" className="u-wordmark" style={{ fontSize: 22 }}>
            Uniap
          </a>

          {desktop && (
            <nav aria-label={t.mainNavLabel} style={{ display: 'flex', gap: 28, fontSize: 15 }}>
              {t.nav.map((n, i) => (
                <a
                  key={n}
                  href={NAV_HREFS[i]}
                  className="u-nav-link"
                  style={{ color: 'var(--ink-2)' }}
                >
                  {n}
                </a>
              ))}
            </nav>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <LangSwitch value={lang} onChange={onLangChange} label={t.langLabel} />
            {desktop ? (
              <Button size="sm" href={authHref}>
                {signedIn ? t.openCabinet : t.cta}
              </Button>
            ) : (
              <IconButton
                ref={toggleRef}
                plain
                label={t.menu}
                aria-expanded={menuOpen}
                aria-controls={menuId}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <Icon name={menuOpen ? 'x' : 'menu'} />
              </IconButton>
            )}
          </div>
        </div>

        {!desktop && (
          <div id={menuId} className="u-header-menu" hidden={!menuOpen}>
            <nav aria-label={t.mainNavLabel} style={{ padding: '12px 18px 16px' }}>
              <ul className="u-header-menu-list">
                {t.nav.map((n, i) => (
                  <li key={n}>
                    <a href={NAV_HREFS[i]} onClick={() => setMenuOpen(false)}>
                      {n}
                    </a>
                  </li>
                ))}
                <li>
                  <Button full href={authHref}>
                    {signedIn ? t.openCabinet : t.cta}
                  </Button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}

export default SiteHeader
